import { ReactElement, useState, useEffect } from 'react';
import { cva } from 'class-variance-authority';
import { cn } from '../utils/cn';

const chipVariants = cva(
  'cr-flex cr-items-center cr-justify-center cr-text-center cr-px-4 cr-py-2 cr-rounded-lg cr-cursor-pointer cr-transition-colors cr-duration-200 cr-text-sm',
  {
    variants: {
      active: {
        true: 'cr-bg-cirrusBlue-35 cr-text-blue-900 hover:cr-bg-cirrusBlue-100',
        false: 'cr-bg-gray-100 cr-text-blue-900 cr-border-2 cr-border-blue-900 hover:cr-bg-gray-300',
      },
    },
    defaultVariants: {
      active: false,
    },
  }
);

export { chipVariants };

type ChipSelectorProps = {
  options: Array<{ label: string; field: string }>;
  storageKey: string;
  onSelectionChange?: (newState: { [key: string]: boolean }) => void;
  className?: string;
  defaultFields?: string[];
};

export default function ChipSelector(props: ChipSelectorProps): ReactElement {
  const { options, storageKey, onSelectionChange, className = '', defaultFields } = props;

  const getInitialState = (): { [key: string]: boolean } => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        const hasAllOptions = options.every((option) => parsed.hasOwnProperty.call(parsed, option.field));
        if (hasAllOptions) {
          return parsed;
        }
      }
    } catch (e) {
      console.error('Error reading state from localStorage:', e);
    }
    if (defaultFields) {
      return options.reduce(
      (acc, option) => ({ ...acc, [option.field]: defaultFields.includes(option.field) }),
      {} as { [key: string]: boolean },
    );
    }
    return options.reduce((acc, option) => ({ ...acc, [option.field]: true }), {} as { [key: string]: boolean });
  };

  const [selectedState, setSelectedState] = useState<{ [key: string]: boolean }>(getInitialState);

  useEffect(() => {
    localStorage.setItem(storageKey, JSON.stringify(selectedState));
  }, [selectedState, storageKey]);

  useEffect(() => {
    onSelectionChange?.(selectedState);
  }, []);

  const toggleAll = () => {
    const allSelected = Object.values(selectedState).every((value) => value);
    const newState = options.reduce(
      (acc, option) => ({ ...acc, [option.field]: !allSelected }),
      {} as { [key: string]: boolean },
    );
    setSelectedState(newState);
    onSelectionChange?.(newState);
  };

  const toggleOption = (field: string) => {
    const newState = {
      ...selectedState,
      [field]: !selectedState[field],
    };
    setSelectedState(newState);
    onSelectionChange?.(newState);
  };

  return (
    <div className={cn('cr-grid cr-grid-cols-4 cr-gap-2 cr-pt-4 md:cr-grid-cols-6 lg:cr-grid-cols-8', className)}>
      <div
        className={chipVariants({ active: Object.values(selectedState).every((value) => value) })}
        onClick={toggleAll}
      >
        All
      </div>
      {options.map((option) => (
        <div
          key={option.field}
          className={chipVariants({ active: selectedState[option.field] })}
          onClick={() => toggleOption(option.field)}
        >
          {option.label}
        </div>
      ))}
    </div>
  );
}
